import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Check, MessageCircle, Pencil, Trash2, UserRound, X, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { WhatsAppDialog } from "@/components/WhatsAppDialog";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";
import { fmtDate, fmtLong } from "@/lib/constants";

const QUICK = [
  { s: "concluido", label: "Concluído", icon: Check, cls: "hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200" },
  { s: "faltou", label: "Faltou", icon: X, cls: "hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200" },
  { s: "atrasado", label: "Atrasado", icon: Clock, cls: "hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200" },
  { s: "agendado", label: "Agendado", icon: CalendarClock, cls: "hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200" },
];

export const AppointmentDetailDialog = ({ appointment: a, open, onOpenChange }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [resched, setResched] = useState(false);
  const [nd, setNd] = useState({ date: "", time: "" });
  const [edit, setEdit] = useState(false);
  const [wa, setWa] = useState(false);

  useEffect(() => {
    if (open && a) { setResched(false); setNd({ date: a.date, time: a.time }); }
  }, [open, a]);

  if (!a) return null;

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); notifyRefresh(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = (s) => run(() => api.patch(`/appointments/${a.id}/status`, { status: s }), "Status atualizado");
  const doResched = () => run(() => api.post(`/appointments/${a.id}/reschedule`, nd), "Consulta reagendada");
  const remove = () => window.confirm("Excluir esta consulta?") && run(() => api.delete(`/appointments/${a.id}`), "Consulta excluída");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="appointment-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl" data-testid="appointment-detail-patient">{a.patient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={a.effective_status} testId="appointment-detail-status" />
              <span className="text-sm text-slate-500">{a.procedure}</span>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 text-sm">
              <p className="font-semibold text-slate-800">{fmtLong(a.date)}</p>
              <p className="text-slate-600">{a.time} · {a.duration} min · {a.patient_phone || "sem telefone"}</p>
              {a.notes && <p className="mt-2 text-slate-500">{a.notes}</p>}
              {a.rescheduled_from && <p className="mt-2 text-xs text-violet-600">Reagendada a partir de outra data</p>}
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Alterar status</p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK.map(({ s, label, icon: Icon, cls }) => (
                  <Button key={s} variant="outline" size="sm" className={`justify-start transition-colors ${cls}`} onClick={() => setStatus(s)} data-testid={`appointment-set-status-${s}`}>
                    <Icon className="mr-1.5 h-4 w-4" /> {label}
                  </Button>
                ))}
              </div>
            </div>
            {resched ? (
              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3">
                <p className="mb-2 text-sm font-semibold text-violet-800">Nova data e horário</p>
                <div className="flex gap-2">
                  <Input type="date" value={nd.date} onChange={(e) => setNd({ ...nd, date: e.target.value })} data-testid="reschedule-date-input" />
                  <Input type="time" value={nd.time} onChange={(e) => setNd({ ...nd, time: e.target.value })} className="w-32" data-testid="reschedule-time-input" />
                </div>
                <Button size="sm" className="mt-2 w-full bg-violet-600 hover:bg-violet-700" onClick={doResched} data-testid="reschedule-confirm-btn">
                  Confirmar reagendamento ({fmtDate(nd.date)} {nd.time})
                </Button>
              </div>
            ) : (
              <Button variant="outline" className="w-full border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setResched(true)} data-testid="appointment-reschedule-btn">
                <CalendarClock className="mr-1.5 h-4 w-4" /> Reagendar
              </Button>
            )}
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setWa(true)} data-testid="appointment-whatsapp-btn">
                <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
              </Button>
              <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); navigate(`/pacientes/${a.patient_id}`); }} data-testid="appointment-open-patient-btn">
                <UserRound className="mr-1.5 h-4 w-4" /> Prontuário
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="appointment-edit-btn">
                <Pencil className="mr-1.5 h-4 w-4" /> Editar
              </Button>
              <Button size="sm" variant="ghost" className="ml-auto text-rose-600 hover:bg-rose-50" onClick={remove} data-testid="appointment-delete-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AppointmentDialog open={edit} onOpenChange={(v) => { setEdit(v); if (!v) onOpenChange(false); }} appointment={a} />
      <WhatsAppDialog open={wa} onOpenChange={setWa} name={a.patient_name} phone={a.patient_phone}
        template="consulta" vars={{ data: fmtDate(a.date), hora: a.time }} />
    </>
  );
};
